import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { GitCompareArrows, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { ConceptList } from "@/components/concepts/ConceptList";
import { CreateConceptDialog } from "@/components/concepts/CreateConceptDialog";
import { InboxSection } from "@/components/inbox/InboxSection";
import { useConceptList } from "@/hooks/useConcepts";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { usePageView } from "@/hooks/usePageView";
import { consumeCheckoutIntent } from "@/lib/checkoutIntent";

export const Route = createFileRoute("/dashboard")({
  component: DashboardPage,
});

function DashboardPage() {
  usePageView("/dashboard");
  const [showCreate, setShowCreate] = useState(false);
  const navigate = useNavigate();
  const { data: concepts, isLoading: conceptsLoading } = useConceptList();
  const hasEnoughConcepts = (concepts?.length ?? 0) >= 2;
  const { data: currentUser } = useCurrentUser();
  const atConceptLimit = currentUser?.plan_limits.max_concepts != null
    && currentUser.concept_count >= currentUser.plan_limits.max_concepts;

  useEffect(() => {
    if (conceptsLoading || concepts === undefined) return;
    // Honor a checkout intent stashed by the landing "Upgrade to Pro" CTA
    // before any onboarding redirect. Clerk drops forceRedirectUrl on fresh
    // signups (→ "/" → here), so this is how straight-to-checkout survives.
    // Consumed regardless of concept count and evaluated first, so the
    // welcome-redirect below can never override it.
    const intent = consumeCheckoutIntent();
    if (intent) {
      navigate({
        to: "/settings/billing",
        search: { checkout: intent.checkout, promo: intent.promo },
        replace: true,
      });
      return;
    }
    if (concepts.length > 0) return;
    const skipKey = currentUser?.auth_id
      ? `paperchase-welcome-skipped-${currentUser.auth_id}`
      : "paperchase-welcome-skipped";
    if (localStorage.getItem(skipKey)) return;
    navigate({ to: "/welcome", replace: true });
  }, [concepts, conceptsLoading, currentUser?.auth_id, navigate]);

  return (
    <div className="space-y-6">
      <InboxSection />
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold">Concepts</h2>
        <div className="flex gap-2">
          <Button
            variant="outline"
            disabled={!hasEnoughConcepts}
            onClick={() => navigate({ to: "/concepts/overlap" })}
            data-testid="compare-concepts-button"
          >
            <GitCompareArrows className="size-4" />
            Compare Concepts
          </Button>
          <Button
            onClick={() => (currentUser?.features?.setup_v2 ? navigate({ to: "/concepts/new" }) : setShowCreate(true))}
            disabled={atConceptLimit}
            title={atConceptLimit ? `You've reached the free plan limit of ${currentUser?.plan_limits.max_concepts} concepts` : undefined}
          >
            <Plus className="size-4" />
            Create Concept
          </Button>
        </div>
      </div>

      <ConceptList />

      <CreateConceptDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
      />
    </div>
  );
}
